import { useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation'

import { toApiPage, withQueryParams } from '@/utils'

import {
  Advertisement,
  AdvertisementResponse,
  AdvertisementSearchParams,
  AdvertisementsParams,
  Attachment,
  CreateAdvertisementSchema,
  PrivateAdvertisement,
  PrivateAdvertisementResponse,
  PrivateAdvertisementsParams,
  UpdateAdvertisementSchema
} from './types'
import { API } from '@/api/endpoints'
import { FetchError, useSWRFetcher } from '@/lib/swr/fetcher'

const SEARCH_BRACKET_ARRAY_KEYS = ['ids', 'features', 'services_included']
const SEARCH_NESTED_BRACKET_ARRAY_KEYS = ['bounding_box']
const DEFAULT_ADVERTISEMENTS_ORDER_BY: AdvertisementsParams['order_by'] = '-date'
const DEFAULT_PRIVATE_ADVERTISEMENTS_ORDER_BY: PrivateAdvertisementsParams['order_by'] =
  '-created_at'

/**
 * GET All
 */
export const useAdvertisements = (params: AdvertisementsParams = {}) => {
  const swrKey = withQueryParams(API.ADVERTISEMENTS, {
    page: params.page !== undefined ? toApiPage(params.page) : undefined,
    per_page: params.per_page,
    order_by: params.order_by || DEFAULT_ADVERTISEMENTS_ORDER_BY
  })

  const { data, error, isLoading, mutate } = useSWR<PrivateAdvertisementResponse>(swrKey)

  return {
    collection: data?.data,
    pagination: data
      ? {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

/**
 * GET All (Private)
 */
export const usePrivateAdvertisements = (params: PrivateAdvertisementsParams = {}) => {
  const swrKey = withQueryParams(API.ADVERTISEMENTS, {
    page: params.page !== undefined ? toApiPage(params.page) : undefined,
    per_page: params.per_page,
    order_by: params.order_by || DEFAULT_PRIVATE_ADVERTISEMENTS_ORDER_BY,
    property_id: params.property_id,
    advertisement_id: params.advertisement_id,
    email: params.email,
    address: params.address,
    q: params.q,
    city_id: params.city_id,
    approved: params.approved !== undefined ? (params.approved ? 1 : 0) : undefined,
    to_approve: params.to_approve !== undefined ? (params.to_approve ? 1 : 0) : undefined
  })

  const { data, error, isLoading, mutate } = useSWR<PrivateAdvertisementResponse>(swrKey)

  return {
    collection: data?.data,
    pagination: data
      ? {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

export const getAdvertisementsSearchKey = (params?: AdvertisementSearchParams) => {
  if (!params) {
    return null
  }

  if (Object.keys(params).length === 0) {
    return API.ADVERTISEMENTS_SEARCH
  }

  // Ensure default ordering for search when caller didn't provide one
  const mergedParams =
    params.order_by !== undefined
      ? params
      : { ...params, order_by: DEFAULT_ADVERTISEMENTS_ORDER_BY }

  return withQueryParams(API.ADVERTISEMENTS_SEARCH, mergedParams, {
    bracketArrayKeys: SEARCH_BRACKET_ARRAY_KEYS,
    nestedBracketArrayKeys: SEARCH_NESTED_BRACKET_ARRAY_KEYS
  })
}

/**
 * Search | GET All
 */
export const useAdvertisementsSearch = (params?: AdvertisementSearchParams) => {
  const paramsKey = JSON.stringify(params)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const swrKey = useMemo(() => getAdvertisementsSearchKey(params), [paramsKey])

  const { data, error, isLoading, mutate } = useSWR<AdvertisementResponse>(swrKey)

  return {
    collection: data?.data,
    pagination: data
      ? {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total
        }
      : undefined,
    isLoading,
    error,
    mutate
  }
}

/**
 * GET by ID
 */
export const useAdvertisement = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<Advertisement>(
    id ? `${API.ADVERTISEMENTS}/${id}` : null
  )
  const { collection } = useAdvertisementSiblings(id)

  return {
    model: data,
    related: collection,
    isLoading,
    error,
    mutate
  }
}

/**
 * GET by ID (Private)
 */
export const usePrivateAdvertisement = (
  propertyId: number | null,
  advertisementId: number | null
) => {
  const swrKey =
    propertyId && advertisementId
      ? `${API.PROPERTIES}/${propertyId}/advertisements/${advertisementId}`
      : null

  const { data, error, isLoading, mutate } = useSWR<PrivateAdvertisement>(swrKey)

  return {
    model: data,
    isLoading,
    error,
    mutate
  }
}

/**
 * CREATE
 */
type CreateAdvertisementOptions = SWRMutationConfiguration<
  PrivateAdvertisement,
  FetchError,
  string,
  { propertyId: number; data: Partial<CreateAdvertisementSchema> },
  PrivateAdvertisement
> & {
  throwOnError?: boolean
}

export const useCreateAdvertisement = (options?: CreateAdvertisementOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isCreating } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      { arg }: { arg: { propertyId: number; data: Partial<CreateAdvertisementSchema> } }
    ) =>
      fetcher<PrivateAdvertisement>(`${url}/${arg.propertyId}/advertisements`, {
        method: 'POST',
        body: JSON.stringify(arg.data)
      }),
    options
  )

  return {
    createAdvertisement: trigger,
    isCreating
  }
}

/**
 * UPDATE
 */
type UpdateAdvertisementOptions = SWRMutationConfiguration<
  PrivateAdvertisement,
  FetchError,
  string,
  { propertyId: number; advertisementId: number; data: Partial<UpdateAdvertisementSchema> },
  PrivateAdvertisement
> & {
  throwOnError?: boolean
}

export const useUpdateAdvertisement = (options?: UpdateAdvertisementOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isUpdating } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: {
          propertyId: number
          advertisementId: number
          data: Partial<UpdateAdvertisementSchema>
        }
      }
    ) =>
      fetcher<PrivateAdvertisement>(
        `${url}/${arg.propertyId}/advertisements/${arg.advertisementId}`,
        {
          method: 'PUT',
          body: JSON.stringify(arg.data)
        }
      ),
    options
  )

  return {
    updateAdvertisement: trigger,
    isUpdating
  }
}

/**
 * DELETE
 */
type DeleteAdvertisementOptions = SWRMutationConfiguration<
  void,
  FetchError,
  string,
  { propertyId: number; advertisementId: number },
  void
> & {
  throwOnError?: boolean
}

export const useDeleteAdvertisement = (options?: DeleteAdvertisementOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isDeleting } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: {
          propertyId: number
          advertisementId: number
        }
      }
    ) =>
      fetcher<void>(`${url}/${arg.propertyId}/advertisements/${arg.advertisementId}`, {
        method: 'DELETE'
      }),
    options
  )

  return {
    deleteAdvertisement: trigger,
    isDeleting
  }
}

/**
 * GET All siblings
 */
export const useAdvertisementSiblings = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<AdvertisementResponse>(
    id ? `${API.ADVERTISEMENTS}/${id}/siblings` : null
  )

  return {
    collection: data?.data,
    isLoading,
    error,
    mutate
  }
}

/**
 * TOGGLE ACTIVE - Attiva/Disattiva annuncio
 */
type ToggleActiveAdvertisementOptions = SWRMutationConfiguration<
  { message: string },
  FetchError,
  string,
  { propertyId: number; advertisementId: number; active: boolean },
  { message: string }
> & {
  throwOnError?: boolean
}

export const useToggleActiveAdvertisement = (options?: ToggleActiveAdvertisementOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isTogglingActive } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: { propertyId: number; advertisementId: number; active: boolean }
      }
    ) =>
      fetcher<{ message: string }>(
        `${url}/${arg.propertyId}/advertisements/${arg.advertisementId}/toggle_active`,
        {
          method: 'POST',
          body: JSON.stringify({ active: arg.active })
        }
      ),
    options
  )

  return {
    toggleActiveAdvertisement: trigger,
    isTogglingActive
  }
}

/**
 * TOGGLE APPROVE - Approva/Rifiuta annuncio
 */
type ToggleApproveAdvertisementOptions = SWRMutationConfiguration<
  { message: string },
  FetchError,
  string,
  { propertyId: number; advertisementId: number; approved: boolean },
  { message: string }
> & {
  throwOnError?: boolean
}

export const useToggleApproveAdvertisement = (options?: ToggleApproveAdvertisementOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isTogglingApprove } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: { propertyId: number; advertisementId: number; approved: boolean }
      }
    ) =>
      fetcher<{ message: string }>(
        `${url}/${arg.propertyId}/advertisements/${arg.advertisementId}/toggle_approve`,
        {
          method: 'POST',
          body: JSON.stringify({ approved: arg.approved })
        }
      ),
    options
  )

  return {
    toggleApproveAdvertisement: trigger,
    isTogglingApprove
  }
}

/**
 * UPLOAD ATTACHMENTS - Carica allegati per un annuncio
 */
type UploadAttachmentsOptions = SWRMutationConfiguration<
  Attachment,
  FetchError,
  string,
  { propertyId: number; advertisementId: number; file: File },
  Attachment
> & {
  throwOnError?: boolean
}

export const useUploadAdvertisementAttachments = (options?: UploadAttachmentsOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isUploading } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: { propertyId: number; advertisementId: number; file: File }
      }
    ) => {
      const formData = new FormData()
      formData.append('image', arg.file)

      return fetcher<Attachment>(
        `${url}/${arg.propertyId}/advertisements/${arg.advertisementId}/attachments`,
        {
          method: 'POST',
          body: formData
        }
      )
    },
    options
  )

  return {
    uploadAttachments: trigger,
    isUploading
  }
}

/**
 * DELETE ATTACHMENT - Elimina un allegato
 */
type DeleteAttachmentOptions = SWRMutationConfiguration<
  void,
  FetchError,
  string,
  { propertyId: number; advertisementId: number; attachmentId: number },
  void
> & {
  throwOnError?: boolean
}

export const useDeleteAdvertisementAttachment = (options?: DeleteAttachmentOptions) => {
  const fetcher = useSWRFetcher()
  const { trigger, isMutating: isDeleting } = useSWRMutation(
    API.PROPERTIES,
    (
      url: string,
      {
        arg
      }: {
        arg: { propertyId: number; advertisementId: number; attachmentId: number }
      }
    ) =>
      fetcher<void>(
        `${url}/${arg.propertyId}/advertisements/${arg.advertisementId}/attachments/${arg.attachmentId}`,
        {
          method: 'DELETE'
        }
      ),
    options
  )

  return {
    deleteAttachment: trigger,
    isDeleting
  }
}

/**
 * RECORD VIEW - Registra visualizzazione annuncio
 * Uses fetch for recording advertisement views
 */
export const recordAdvertisementView = async (
  advertisementId: number,
  statsToken: string | null
): Promise<boolean> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (statsToken) headers['X-Stats-Token'] = statsToken

    const response = await fetch(`/agr-api/advertisements/${advertisementId}/view`, {
      method: 'POST',
      headers
    })

    if (!response.ok) {
      console.warn('[recordAdvertisementView] Request failed:', response.status)
      return false
    }

    return true
  } catch (error) {
    console.warn('[recordAdvertisementView] Error:', error)
    return false
  }
}

/**
 * Hook to automatically record advertisement view when the component mounts
 * Uses fetch for fire-and-forget analytics
 */
export const useRecordAdvertisementView = (advertisementId: number | null) => {
  const hasRecorded = useRef(false)

  useEffect(() => {
    // Only record once per mount, and only if we have a valid ID
    if (!advertisementId || hasRecorded.current) return

    // Get existing token from localStorage
    const existingToken =
      typeof window !== 'undefined' ? localStorage.getItem('sais_stats_token') : null

    recordAdvertisementView(advertisementId, existingToken)
    hasRecorded.current = true
  }, [advertisementId])
}
